import { PaymentModuleId } from '@foundation/apis/payments';
import { SupportedLanguage } from '@foundation/localization';
import Clarity from '@microsoft/clarity';
import { useAuth } from '@shared/auth';
import { useTranslation } from '@shared/localization';
import cn from 'clsx';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  disableUIBlocker,
  enableUIBlocker,
  Heading,
  PaymentsLayout,
  PlusIcon,
  PrimaryButton,
} from '@/components/common';
import { RedirectStatus } from '@/components/contexts/params';
import { useUserFriendlyPaymentModuleName } from '@/hooks/useUserFriendlyPaymentModuleName';
import { useUserFriendlyPrefixForInstrumentBrand } from '@/hooks/useUserFriendlyPrefixForInstrumentBrands';
import { triggerErrorToast, triggerInfoToast } from '@/libs/toasts';
import { PaymentIntentFlow } from '@/schemas/PaymentIntent';
import BraintreeCardService from '@/services/braintree/BraintreeCardService';
import BraintreeGooglePayService from '@/services/braintree/BraintreeGooglePayService';
import BraintreePayPalService from '@/services/braintree/BraintreePayPalService';
import BraintreeSepaService from '@/services/braintree/BraintreeSepaService';
import { BraintreeError } from '@/services/braintree/errors/BraintreeError';
import { isUnsupportedCard } from '@/services/braintree/helpers/BraintreeValidationHelper';
import LSVService from '@/services/directdebit/LSVService';
import PostFinanceService from '@/services/directdebit/PostFinanceService';
import SepaService from '@/services/directdebit/SepaService';
import FlutterwaveCardService from '@/services/flutterwave/FlutterwaveCardService';
import Logger, { LoggerOrigin } from '@/services/Logger';
import NavigationService, {
  InternalPage,
} from '@/services/navigation/NavigationService';
import NuveiCardService from '@/services/nuvei/NuveiCardService';
import { type IManagePaymentInstrument } from '@/services/payments/instruments/MandateReference';
import PaymentStore from '@/services/payments/PaymentStore';
import TelemetryService from '@/services/telemetry/TelemetryService';
import WorldpayCardService from '@/services/worldpay/WorldpayCardService';
import WorldpayJCBService from '@/services/worldpay/WorldpayJCBService';
import { redirect } from '@/utils/redirect';

import { CancelPaymentButton } from './utils/CancelPaymentButton';
import { computeRadioOptions } from './utils/helper';
import { NewInstrumentButton } from './utils/NewInstrumentButton';
import { PayButton } from './utils/PayButton';
import { RedirectUrlButton } from './utils/RedirectUrlButton';
import { RadioGroup } from '../../common/forms/inputs/RadioGroup';

interface IStoredInstrumentsPageProps {
  readonly instruments: ReadonlyArray<IManagePaymentInstrument>;
  readonly flow: PaymentIntentFlow;
  readonly canAddInstruments?: boolean;
}

const styles = {
  container:
    'lg:px-[32px] lg:py-[20px] p-[20px] relative mt-xs first:mt-0 flex items-center ring-[1px] ring-silver-700 rounded-field bg-white min-h-[84px]',
  disabledContainer: 'bg-secondary',
  containerFocus: 'hover:ring-silver-700 hover:!ring-[2px] focused-container',
  containerSelected: 'bg-secondary !ring-primary !ring-[2px]',
};

export function StoredInstrumentsPage({
  instruments,
  flow,
  canAddInstruments = true,
}: IStoredInstrumentsPageProps) {
  const { accessToken, userId } = useAuth();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const paymentModuleT = useUserFriendlyPaymentModuleName();

  const [selectedInstrumentId, setSelectedInstrumentId] = useState<string>();
  const instrumentBrandPrefixer = useUserFriendlyPrefixForInstrumentBrand();
  const [searchParams, setSearchParams] = useSearchParams();

  const triggerAuthorizeForInstrument = useCallback(
    (instrumentId: string) => {
      const instrument = instruments.find((item) => item.id === instrumentId);

      if (!instrument) {
        return;
      }

      let brandPrefix = '';

      if (instrument.brand !== undefined) {
        const brand = instrumentBrandPrefixer(instrument.brand);
        if (brand !== undefined) {
          brandPrefix = brand.toString().toUpperCase();
        }
      }

      let promise = Promise.resolve();
      const currentPage = NavigationService.getInternalPage();
      NavigationService.navigateTo({ page: InternalPage.StatusProcessing });

      switch (instrument.moduleId) {
        case PaymentModuleId.BraintreeCard:
          enableUIBlocker();
          promise =
            BraintreeCardService.verifyAndAuthorizeInstrument(instrument);
          break;
        case PaymentModuleId.BraintreeGooglePay:
          promise = BraintreeGooglePayService.authorizeInstrument(instrument);
          break;
        case PaymentModuleId.BraintreePayPal:
          promise = BraintreePayPalService.authorize(undefined, instrument.id);
          break;
        case PaymentModuleId.BraintreeSepa:
          promise = BraintreeSepaService.authorizeInstrument(instrument);
          break;
        case PaymentModuleId.WorldpayCard:
          promise = new Promise((_, reject) => {
            //resolve argument (1-st) will never be called, but we need it to to keep order of the params
            WorldpayCardService.getRedirectUrl(
              language as SupportedLanguage,
              accessToken ?? '',
              instrument.id,
            )
              .then((s) => {
                redirect(s);
              })
              .catch((err) => {
                reject(err);
              });
          });
          break;
        case PaymentModuleId.WorldpayJCB:
          promise = new Promise((_, reject) => {
            //resolve argument (1-st) will never be called, but we need it to to keep order of the params
            WorldpayJCBService.getRedirectUrl(
              language as SupportedLanguage,
              accessToken ?? '',
              instrument.id,
            )
              .then((s) => {
                redirect(s);
              })
              .catch((err) => {
                reject(err);
              });
          });
          break;
        case PaymentModuleId.FlutterwaveCreditCard:
          promise = new Promise((_, reject) => {
            //resolve argument (1-st) will never be called, but we need it to to keep order of the params
            FlutterwaveCardService.getRedirectUrl(
              language as SupportedLanguage,
              accessToken ?? '',
              instrument.id,
            )
              .then((s) => {
                redirect(s);
              })
              .catch((err) => {
                reject(err);
              });
          });
          break;
        case PaymentModuleId.Sepa:
          promise = SepaService.authorize(
            instrument.id,
            accessToken ?? '',
            instrument.mandateReference,
          );
          break;
        case PaymentModuleId.DirectDebitLSV:
          promise = LSVService.authorize(
            instrument.id,
            accessToken ?? '',
            instrument.mandateReference,
          );
          break;
        case PaymentModuleId.DirectDebitPostFinance:
          promise = PostFinanceService.authorize(
            instrument.id,
            accessToken ?? '',
            instrument.mandateReference,
          );
          break;
        case PaymentModuleId.NuveiCreditCard:
          promise = NuveiCardService.initTransactionAndSafeCharge(
            instrument.id,
            userId,
            accessToken ?? '',
          )
            .then(() => NuveiCardService.createCITPayment(userId))
            .then(() =>
              NuveiCardService.confirmPayment(
                PaymentStore.paymentIntentId,
                accessToken ?? '',
              ),
            );
          break;
        default: {
          const exception = new Error(
            `Instrument for moduleId ${instrument.moduleId} should not be stored! How did this happen?`,
          );
          exception.name = 'UnexpectedModuleIdError';
          promise = Promise.reject(exception);
          Logger.error(
            LoggerOrigin.ReactComponent,
            'Unexpected moduleId.',
            exception,
          );
        }
      }

      promise
        .then(() => {
          NavigationService.navigateTo({ page: InternalPage.StatusSuccess });
          Clarity.setTag('pay', [
            RedirectStatus.Success,
            'Stored instrument used',
            paymentModuleT(instrument.moduleId),
          ]);
        })
        .catch((err: Error) => {
          if (
            PaymentStore.externalPreselectedInstrument !== undefined &&
            searchParams.has('instrumentId')
          ) {
            searchParams.delete('instrumentId');
            setSearchParams(searchParams);
          }

          PaymentStore.denyPreselectedInstrumentPayment();
          TelemetryService.trackException(err, 'StoredInstrumentsPage');
          Logger.error(
            LoggerOrigin.ReactComponent,
            'Failed to authorize instrument',
            instrument,
            err,
          );
          NavigationService.navigateTo(currentPage);

          if (NuveiCardService.isNuveiError(err)) {
            TelemetryService.trackException(err, 'NuveiCreditCard', {
              pid: PaymentStore.paymentIntentId,
            });

            if (NuveiCardService.isNuveiTransactionCancelledByUser(err)) {
              triggerInfoToast(
                t('Select a new payment method and proceed to pay.'),
              );
              Clarity.setTag('pay', [
                RedirectStatus.Canceled,
                'Stored instrument used',
                paymentModuleT(instrument.moduleId),
              ]);
            } else {
              triggerErrorToast(
                t('Something went wrong!'),
                t(
                  'Operation failed. Please try again with a different payment method.',
                ),
              );
              Clarity.setTag('pay', [
                RedirectStatus.Failure,
                'Stored instrument used',
                paymentModuleT(instrument.moduleId),
              ]);
            }
          } else if (err !== undefined && err.name === 'BraintreeError') {
            const braintreeError = err as BraintreeError;
            const isUnsupportedMethodByBraintreeMerchant =
              braintreeError !== undefined && isUnsupportedCard(braintreeError);

            if (isUnsupportedMethodByBraintreeMerchant) {
              if (brandPrefix === '') {
                triggerErrorToast(
                  t('Unsupported payment method!'),
                  t(
                    'The selected payment method is not available for this transaction.',
                  ),
                );
              } else {
                triggerErrorToast(
                  t('Unsupported payment method!'),
                  t(
                    '{{brandPrefix}} payment method cannot be used for this transaction.',
                    { brandPrefix: brandPrefix },
                  ),
                );
              }
              Clarity.setTag('pay', [
                RedirectStatus.Failure,
                'Stored instrument used',
                paymentModuleT(instrument.moduleId),
              ]);
            }
          } else {
            triggerErrorToast(
              t('Something went wrong!'),
              t(
                'Operation failed. Please try again with a different payment method.',
              ),
            );

            Clarity.setTag('pay', [
              RedirectStatus.Failure,
              'Stored instrument used',
              paymentModuleT(instrument.moduleId),
            ]);
          }
        })
        .finally(() => disableUIBlocker());
    },

    [],
  );

  const payWithSelectedMethod = useCallback(() => {
    if (selectedInstrumentId !== undefined) {
      triggerAuthorizeForInstrument(selectedInstrumentId);
    }
  }, [selectedInstrumentId, triggerAuthorizeForInstrument]);

  useEffect(() => {
    if (
      PaymentStore.externalPreselectedInstrument !== undefined &&
      PaymentStore.isPreselectedInstrumentPaymentAllowed
    ) {
      payWithSelectedMethod();
    }
  }, [payWithSelectedMethod]);

  const radioOptions = useMemo(
    () => computeRadioOptions(instruments),
    [instruments],
  );

  const { pageFooter, pageNavigation, pageMainAction } = useMemo(() => {
    if (flow === PaymentIntentFlow.Manage) {
      return {
        pageTitle: t('Edit your payment methods'),
        pageFooter: null,
        pageNavigation:
          PaymentStore.redirectUrl !== undefined ? <RedirectUrlButton /> : null,
        pageMainAction: null,
      };
    }

    return {
      pageTitle: t('Select your payment method'),
      pageFooter: <CancelPaymentButton className="!text-black" />,
      pageNavigation: null,
      pageMainAction: instruments.length ? (
        <PayButton
          key={selectedInstrumentId}
          className={'w-max'}
          disabled={
            radioOptions.length === 0 || selectedInstrumentId === undefined
          }
          onClick={() => {
            payWithSelectedMethod();
          }}
        />
      ) : (
        <PrimaryButton
          className="flex items-center w-fit justify-center"
          onClick={() => {
            NavigationService.navigateTo({
              page: InternalPage.PaymentMethods,
            });
          }}
        >
          <PlusIcon />
          {t('Add a new Payment Method')}
        </PrimaryButton>
      ),
    };
  }, [
    flow,
    t,
    selectedInstrumentId,
    radioOptions.length,
    payWithSelectedMethod,
    instruments.length,
  ]);

  const pageContent = useMemo(() => {
    if (flow === PaymentIntentFlow.Manage) {
      return (
        <>
          {(radioOptions ?? []).map((opt) => {
            return (
              <div
                key={opt.id}
                className={cn(
                  styles.container,
                  opt.disabled && styles.disabledContainer,
                )}
              >
                {opt.label}
              </div>
            );
          })}
        </>
      );
    }

    if (!instruments.length) {
      return (
        <div className="flex flex-col items-center text-center bg-white border-[1px] border-silver-700 rounded-xl p-md">
          <img
            src="/images/icon-add-new-card.png"
            alt="no instruments"
            className="w-[120px] h-[120px] mb-sm"
          />
          <Heading className="text-2xl mb-sm">
            {t('Add a new Payment Method')}
          </Heading>
        </div>
      );
    }

    return (
      <RadioGroup
        preSelectedValue={
          PaymentStore.preSelectedInstrument === undefined
            ? undefined
            : PaymentStore.preSelectedInstrument.id
        }
        onSelectionChange={(instrumentIdAsString) =>
          instrumentIdAsString
            ? setSelectedInstrumentId(instrumentIdAsString)
            : setSelectedInstrumentId(undefined)
        }
        name="stored-instruments"
        options={radioOptions ?? []}
      />
    );
  }, [flow, radioOptions, instruments.length, t]);
  return (
    <div
      className={cn(
        PaymentStore.externalPreselectedInstrument !== undefined &&
          PaymentStore.isPreselectedInstrumentPaymentAllowed
          ? 'invisible'
          : 'visible',
      )}
    >
      <PaymentsLayout
        footer={pageFooter}
        navigation={pageNavigation}
        mainAction={pageMainAction}
        flow={flow}
      >
        {pageContent}
        {canAddInstruments && !!instruments.length && (
          <NewInstrumentButton flow={flow} />
        )}
      </PaymentsLayout>
    </div>
  );
}
